import type {
  ChangeUserStatusPayload,
  CreateRolePayload,
  InviteUserPayload,
  ManagedUser,
  PaginatedResponse,
  PermissionGroup,
  Role,
  UpdateRolePayload,
  UpdateUserPayload,
  UsersQueryParams,
} from "@/types/rbac";
import { apiClient } from "./client";

function buildQueryString(params: UsersQueryParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set("page", String(params.page));
  searchParams.set("pageSize", String(params.pageSize));
  if (params.search) {
    searchParams.set("search", params.search);
  }
  if (params.status && params.status !== "all") {
    searchParams.set("status", params.status);
  }
  if (params.roleId && params.roleId !== "all") {
    searchParams.set("roleId", params.roleId);
  }
  if (params.sortBy) {
    searchParams.set("sortBy", params.sortBy);
  }
  if (params.sortOrder) {
    searchParams.set("sortOrder", params.sortOrder);
  }
  return searchParams.toString();
}

export const adminApi = {
  // --- Users ---
  getUsers(params: UsersQueryParams) {
    const qs = buildQueryString(params);
    return apiClient.get<PaginatedResponse<ManagedUser>>(`/admin/users?${qs}`);
  },

  getUser(userId: string) {
    return apiClient.get<ManagedUser>(`/admin/users/${userId}`);
  },

  inviteUser(payload: InviteUserPayload) {
    return apiClient.post<ManagedUser>("/admin/users/invite", payload);
  },

  updateUser(userId: string, payload: UpdateUserPayload) {
    return apiClient.put<ManagedUser>(`/admin/users/${userId}`, payload);
  },

  changeUserStatus(userId: string, payload: ChangeUserStatusPayload) {
    return apiClient.post<ManagedUser>(
      `/admin/users/${userId}/status`,
      payload,
    );
  },

  deleteUser(userId: string) {
    return apiClient.delete<void>(`/admin/users/${userId}`);
  },

  sendPasswordResetLink(userId: string) {
    return apiClient.post<void>(`/admin/users/${userId}/send-reset-link`);
  },

  /** Forget every trusted device of the agent — the next login anywhere asks for the e-mail code. */
  resetTrustedDevices(userId: string): Promise<{ removed: number }> {
    return apiClient.post<{ removed: number }>(
      `/admin/users/${userId}/trusted-devices/reset`,
    );
  },

  // --- Roles ---
  getRoles() {
    return apiClient.get<Role[]>("/admin/roles");
  },

  getRole(roleId: string) {
    return apiClient.get<Role>(`/admin/roles/${roleId}`);
  },

  createRole(payload: CreateRolePayload) {
    return apiClient.post<Role>("/admin/roles", payload);
  },

  updateRole(roleId: string, payload: UpdateRolePayload) {
    return apiClient.put<Role>(`/admin/roles/${roleId}`, payload);
  },

  deleteRole(roleId: string) {
    return apiClient.delete<void>(`/admin/roles/${roleId}`);
  },

  // --- Permissions ---
  getPermissions() {
    return apiClient.get<PermissionGroup[]>("/admin/permissions");
  },
};
